/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {CSSProperties, memo, ReactNode, RefObject, useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import PageScrim from '../components/backdrop/PageScrim';
import {TertiaryButton} from '../components/buttons/current/TertiaryButton';
import {IconButton} from '../components/buttons/IconButton';
import PrimaryButton from '../components/buttons/PrimaryButton';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {SystemDeleteIcon} from '../icons/SystemDeleteIcon';
import {dlsColor, dlsZIndex} from '../theme/theme';
import {IconSize} from '../types/types';
import {renderInPortal} from './renderInPortal';

export interface AppliedFilterClearAllConfirmationProps {
  classes: Record<string, string>;
  clearAllLabel: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  // the Clear All button it opens under - without one, it opens at the
  // window's top right - or, with `centered`, the panel it centres over,
  // the window without one ...
  anchorRef?: RefObject<HTMLElement>;
  // which of the anchor's edges it lines up with - its left edge by default,
  // or its right edge (`end`) ...
  anchorAlign?: ConfirmationAnchorAlign;
  centered?: boolean;
  // the primary action's icon - a bin by default, `null` for none ...
  primaryIcon?: ReactNode;
  primaryDisabled?: boolean;
  // the secondary action's label and handler - "No, keep filters" and
  // `onCancel` by default ...
  secondaryLabel?: string;
  onSecondary?: () => void;
  // the layer it shares with its scrim - the filter bar's by default ...
  zIndex?: number;
}

export enum ConfirmationAnchorAlign {
  Start = 'start',
  End = 'end'
}

// the gap the design leaves between a control and what it opens ...
const ANCHOR_OFFSET = 8;
// keeps the confirmation clear of the window's edges ...
const WINDOW_EDGE = 20;
// the confirmation's own width and least height, as its styles set them ...
const CONFIRMATION_WIDTH  = 318;
const CONFIRMATION_HEIGHT = 196;

// centred in the window, for a confirmation with no control to open from ...
const CENTERED_PLACEMENT: CSSProperties = {
  top       : '50%',
  left      : '50%',
  right     : 'auto',
  transform : 'translate(-50%, -50%)'
};

// centred over a panel, as the window centres it ...
const getCenteredPlacement = (rect: DOMRect): CSSProperties => ({
  ...CENTERED_PLACEMENT,
  top  : rect.top + rect.height / 2,
  left : rect.left + rect.width / 2
});

// where the confirmation sits by its anchor - centred over it with
// `centered` (the window when it has none), else under it, or over it when
// the window has no room below (a drawer footer's button) - its left edge on
// the anchor's, or its right edge with `End`, moved only as far as it must
// to stay in the window ...
const measurePlacement = (
  anchor: HTMLElement | null | undefined,
  align: ConfirmationAnchorAlign = ConfirmationAnchorAlign.Start,
  centered = false
): CSSProperties | undefined => {
  const rect = anchor?.getBoundingClientRect?.();
  let placement: CSSProperties | undefined = centered ? CENTERED_PLACEMENT : undefined;
  if (rect && centered) {
    placement = getCenteredPlacement(rect);
  } else if (rect) {
    const left    = align === ConfirmationAnchorAlign.End ? rect.right - CONFIRMATION_WIDTH : rect.left;
    const below   = rect.bottom + ANCHOR_OFFSET;
    const hasRoom = below + CONFIRMATION_HEIGHT <= window.innerHeight - WINDOW_EDGE;
    placement = {
      top   : hasRoom ? below : Math.max(rect.top - ANCHOR_OFFSET - CONFIRMATION_HEIGHT, WINDOW_EDGE),
      left  : Math.max(Math.min(left, window.innerWidth - CONFIRMATION_WIDTH - WINDOW_EDGE), WINDOW_EDGE),
      right : 'auto'
    };
  }
  return placement;
};

// a frame that measures the same place keeps the one it had ...
const isSamePlacement = (current: CSSProperties | undefined, next: CSSProperties | undefined): boolean => (
  current?.top === next?.top && current?.left === next?.left
);

/**
 * Displays the current theme's filter confirmation - the applied filter
 * bar's Clear All, and the filter drawer's own confirmations alike - by the
 * control it opens from (`anchorRef`), or centred (`centered`) over that
 * panel - the window without one - with the page content dimmed behind it.
 * The anchor is followed every frame while the confirmation is open, as the
 * bar re-lays its chips out.
 *
 * ```typescript
 * import {AppliedFilterClearAllConfirmation} from '@ion/dls/src/filters/AppliedFilterClearAllConfirmation';
 *
 * <AppliedFilterClearAllConfirmation classes={classes}
 *   clearAllLabel='Clear All'
 *   message='This will remove all applied filters.'
 *   title='Clear all filters?'
 *   anchorRef={clearAllRef}
 *   onCancel={onCancel}
 *   onConfirm={onConfirm} />
 * ```
 */
export const AppliedFilterClearAllConfirmation = ({
  classes,
  clearAllLabel,
  message,
  onCancel,
  onConfirm,
  title,
  anchorRef,
  anchorAlign,
  centered = false,
  primaryDisabled = false,
  primaryIcon = <SystemDeleteIcon color={dlsColor('white')} size={IconSize.small} />,
  secondaryLabel = translate('filters:selectedBar.keepFilters'),
  onSecondary = onCancel,
  zIndex = Number(dlsZIndex('trackingFilterSelectedFiltersConfirmation'))
}: AppliedFilterClearAllConfirmationProps) => {
  const [placement, setPlacement] = useState<CSSProperties | undefined>(function() {
    return measurePlacement(anchorRef?.current, anchorAlign, centered);
  });

  useEffect(() => {
    let frame = 0;
    const track = function() {
      setPlacement(function(current) {
        const next = measurePlacement(anchorRef?.current, anchorAlign, centered);
        return isSamePlacement(current, next) ? current : next;
      });
      frame = window.requestAnimationFrame(track);
    };
    if (anchorRef) {
      frame = window.requestAnimationFrame(track);
    }
    return function() {
      window.cancelAnimationFrame(frame);
    };
  }, [anchorAlign, anchorRef, centered]);

  // Esc closes it, as its X and a click on the scrim do - caught before it
  // reaches the page, so a drawer it opens over keeps its own Esc out of it
  // and stays open ...
  useEffect(() => {
    const onKeyDown = function(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return function() {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onCancel]);

  const confirmation = (
    <div className={classes.confirmation} style={placement}>
      <div className={classes.confirmationHeader}>
        <span className={classes.confirmationTitle}>{title}</span>
        <IconButton isControlButton
          icon={<MaterialExitCloseIcon color={dlsColor('grey1')} size={IconSize.small} />}
          onClick={onCancel} />
      </div>
      <div className={classes.confirmationMessage}>{message}</div>
      <div className={classes.confirmationActions}>
        <TertiaryButton classes={{root: classes.tertiaryRoot}}
          onClick={onSecondary}>
          {secondaryLabel}
        </TertiaryButton>
        <PrimaryButton classes={{root: classes.primaryRoot}}
          icon={primaryIcon}
          disabled={primaryDisabled}
          onClick={onConfirm}>
          {clearAllLabel}
        </PrimaryButton>
      </div>
    </div>
  );
  // the scrim and the confirmation share one layer - the confirmation's own
  // - so the scrim dims everything below it, the filter bar's chips too ...
  const content = (
    <div style={{position: 'relative', zIndex}}>
      <PageScrim onClick={onCancel} />
      {confirmation}
    </div>
  );

  return renderInPortal(content);
};

export default memo(AppliedFilterClearAllConfirmation);
